import React from "react";

const BRAND = "SOVERENX SYSTEMS";

const nodeStyleFor = (type, accent) => {
  switch (type) {
    case "start":
    case "end":
      return { background: accent, color: "#fff", borderColor: accent, borderRadius: 999 };
    case "decision":
      return { background: "#FFFBEB", color: "#78350F", borderColor: "#FBBF24" };
    default:
      return { background: "#fff", color: "#1E293B", borderColor: "#CBD5E1" };
  }
};

const FlowBlock = ({ flowchart, accent }) => {
  if (!flowchart || flowchart.length === 0) return null;
  return (
    <div id="flowchart-capture" className="bg-white py-4 px-2 flex flex-col items-center" data-testid="preview-flowchart">
      {flowchart.map((n, i) => (
        <React.Fragment key={n.id}>
          <div
            className="min-w-[180px] max-w-[300px] text-center px-5 py-3 border text-sm font-medium shadow-sm"
            style={nodeStyleFor(n.type, accent)}
          >
            {n.label}
          </div>
          {i < flowchart.length - 1 && (
            <div className="relative" style={{ width: 2, height: 28, background: accent }}>
              <span
                className="absolute left-1/2 bottom-0"
                style={{
                  transform: "translateX(-50%)",
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderTop: `7px solid ${accent}`,
                }}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const DocumentPreview = ({ title, docTypeLabel, metadata, sections, flowchart, logoUrl, accentColor, footerText }) => {
  const accent = accentColor || "#002FA7";
  const footer = footerText || "Confidential Internal Document";
  return (
    <div
      className="w-full max-w-[816px] mx-auto bg-white border border-slate-200 shadow-[0_8px_40px_rgb(0,0,0,0.10)] min-h-[1056px] flex flex-col"
      data-testid="document-canvas"
    >
      {/* Header band */}
      <div className="text-white px-12 py-6 flex items-start justify-between" style={{ background: accent }}>
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <div className="inline-flex items-center rounded-md bg-black px-3 py-2">
              <img src={logoUrl} alt="Soverenx Systems" className="h-9 w-auto object-contain" data-testid="preview-logo" crossOrigin="anonymous" />
            </div>
          ) : (
            <div>
              <div className="font-heading font-black text-xl tracking-tight leading-none">{BRAND}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/70 mt-1">Internal Documentation</div>
            </div>
          )}
        </div>
        <div className="text-right text-[11px] leading-relaxed text-white/80">
          <div className="font-semibold text-white">{docTypeLabel}</div>
          <div>Doc No: {metadata.document_number || "—"}</div>
          <div>Version: {metadata.version || "—"}</div>
          <div>Date: {metadata.date || "—"}</div>
        </div>
      </div>

      {/* Body */}
      <div className="px-12 py-10 flex-1">
        <h1 className="font-doc text-4xl font-bold text-slate-900 leading-tight" data-testid="preview-title">
          {title || "Untitled Document"}
        </h1>
        <div className="text-xs text-slate-500 mt-2 mb-8 border-b border-slate-200 pb-4">
          Document No. {metadata.document_number || "—"} · Version {metadata.version || "—"} · {metadata.date || "—"}
        </div>

        {sections && sections.length > 0 ? (
          <div className="space-y-6" data-testid="preview-sections">
            {sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-doc text-lg font-bold mb-1.5" style={{ color: accent }}>
                  {i + 1}. {s.heading}
                </h2>
                <div className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap">
                  {s.content || <span className="text-slate-400 italic">No content yet.</span>}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="text-slate-400 text-sm py-20 text-center border border-dashed border-slate-200">
            Paste your rough notes and click <span className="font-semibold">Generate Document</span> to see the
            structured output here.
          </div>
        )}

        {flowchart && flowchart.length > 0 && (
          <div className="mt-10">
            <h2 className="font-doc text-lg font-bold mb-3" style={{ color: accent }}>Process Flowchart</h2>
            <FlowBlock flowchart={flowchart} accent={accent} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-12 py-4 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-500">
        <span>{BRAND} — {footer}</span>
        <span>Uncontrolled when printed</span>
      </div>
    </div>
  );
};

export default DocumentPreview;
